import React, { useState } from "react";
import { format } from "date-fns";
import AsyncSelect from "react-select/async";
import { 
  File, 
  CheckCircle, 
  Calendar, 
  DollarSign, 
  FileText, 
  AlertTriangle,
  Eye,
  Hash,
  MessageSquare,
  Calculator,
  Image,
  Paperclip,
  Edit3,
  Plus,
  Trash2,
  Save,
  X
} from "lucide-react";

const PreviewStep = ({
  entry,
  onChange,
  templates,
  accounts = [],
  accountsLoading = false,
  accountsError = null,
  getAccountByNumber,
  getAccountsByType,
  searchAccounts
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editableEntries, setEditableEntries] = useState([]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  // Evaluate formula with parameters
  const evaluateFormula = (formula, parameters) => {
    if (!formula) return 0;
    
    try {
      let expression = formula;
      Object.keys(parameters).forEach(param => {
        const value = parameters[param] || 0;
        expression = expression.replace(new RegExp(param, 'g'), value);
      });
      
      return eval(expression);
    } catch (error) {
      console.error('Error evaluating formula:', formula, error);
      return 0;
    }
  };

  // Generate journal entries from template with sorting
  const generateJournalEntries = () => {
    if (!entry.templateData || !entry.templateData.transactions) {
      return [];
    }

    const entries = [];
    
    entry.templateData.transactions.forEach((transaction, index) => {
      let accountNumber = transaction.account_number;
      let accountName = transaction.account_name;

      if (transaction.is_user_defined) {
        const userAccountKey = `user_account_${index}`;
        const selectedAccount = entry.userDefinedAccounts?.[userAccountKey];
        if (selectedAccount) {
          accountNumber = selectedAccount;
          const accountInfo = accounts.find(acc => acc.value === selectedAccount);
          accountName = accountInfo ? accountInfo.label.split(' - ')[1] : `Konto ${selectedAccount}`;
        } else {
          accountNumber = "Ej valt";
          accountName = "";
        }
      }

      const debitAmount = transaction.debit_formula 
        ? evaluateFormula(transaction.debit_formula, entry.parameters)
        : 0;
      
      const creditAmount = transaction.credit_formula 
        ? evaluateFormula(transaction.credit_formula, entry.parameters)
        : 0;

      if (debitAmount > 0 || creditAmount > 0) {
        entries.push({
          id: `entry_${index}`,
          account: accountNumber,
          accountName: accountName,
          debit: debitAmount,
          credit: creditAmount,
          isUserDefined: transaction.is_user_defined,
          originalIndex: index
        });
      }
    });

    // Sort entries by account number
    return entries.sort((a, b) => {
      if (a.account === "Ej valt" && b.account !== "Ej valt") return 1;
      if (b.account === "Ej valt" && a.account !== "Ej valt") return -1;
      if (a.account === "Ej valt" && b.account === "Ej valt") return 0;
      
      const accountA = parseInt(a.account, 10);
      const accountB = parseInt(b.account, 10);
      
      if (!isNaN(accountA) && !isNaN(accountB)) {
        return accountA - accountB;
      }
      
      return a.account.localeCompare(b.account);
    });
  };

  // Load account options for AsyncSelect
  const loadAccountOptions = (inputValue) => {
    return new Promise((resolve) => {
      const filteredOptions = searchAccounts ? searchAccounts(inputValue) :
        accounts.filter(account =>
          account.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          account.value.includes(inputValue)
        );

      setTimeout(() => {
        resolve(filteredOptions);
      }, 200);
    });
  };

  // Get current account option for AsyncSelect
  const getCurrentAccountOption = (accountNumber) => {
    if (!accountNumber || accountNumber === "Ej valt") return null;

    if (getAccountByNumber) {
      return getAccountByNumber(accountNumber);
    }

    return accounts.find(acc => acc.accountNumber === accountNumber || acc.value === accountNumber) ||
      { value: accountNumber, label: `${accountNumber} - Okänt konto` };
  };

  // Open edit modal with current entries
  const openEditModal = () => {
    const currentEntries = generateJournalEntries();
    const entriesWithAccountOptions = currentEntries.map(entry => ({
      ...entry,
      accountOption: getCurrentAccountOption(entry.account)
    }));
    setEditableEntries(entriesWithAccountOptions);
    setShowEditModal(true);
  };

  // Add new journal entry
  const addNewEntry = () => {
    const newEntry = {
      id: `custom_${Date.now()}`,
      account: "",
      accountName: "",
      accountOption: null,
      debit: 0,
      credit: 0,
      isUserDefined: true,
      isCustom: true
    };
    setEditableEntries([...editableEntries, newEntry]);
  };

  // Remove journal entry
  const removeEntry = (entryId) => {
    setEditableEntries(editableEntries.filter(entry => entry.id !== entryId));
  };

  const updateEntry = (entryId, field, value) => {
    setEditableEntries(editableEntries.map(entry => {
      if (entry.id === entryId) {
        const updatedEntry = { ...entry, [field]: value };

        // If account option is changed, update account number and name
        if (field === 'accountOption') {
          if (value) {
            updatedEntry.account = value.value;
            updatedEntry.accountName = value.label.split(' - ')[1] || '';
          } else {
            updatedEntry.account = "";
            updatedEntry.accountName = "";
          }
        }

        // If debit is entered, clear credit
        if (field === 'debit' && value > 0) {
          updatedEntry.credit = 0;
        }

        // If credit is entered, clear debit
        if (field === 'credit' && value > 0) {
          updatedEntry.debit = 0;
        }

        return updatedEntry;
      }
      return entry;
    }));
  };

  const saveEditedEntries = () => {
    // Sort the entries before saving
    const sortedEntries = [...editableEntries].sort((a, b) => {
      if (a.account === "Ej valt" && b.account !== "Ej valt") return 1;
      if (b.account === "Ej valt" && a.account !== "Ej valt") return -1;
      if (a.account === "Ej valt" && b.account === "Ej valt") return 0;

      const accountA = parseInt(a.account, 10);
      const accountB = parseInt(b.account, 10);

      if (!isNaN(accountA) && !isNaN(accountB)) {
        return accountA - accountB;
      }

      return a.account.localeCompare(b.account);
    });

    onChange({
      ...entry,
      customJournalEntries: sortedEntries
    });

    setShowEditModal(false);
  };

  // Calculate totals for editable entries
  const calculateEditableTotals = () => {
    const totalDebit = editableEntries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0);
    const totalCredit = editableEntries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0);
    return { totalDebit, totalCredit };
  };

  // Find template display name
  const getTemplateDisplayName = () => {
    if (entry.templateData) {
      return entry.templateData.template_name;
    }
    
    const template = templates.find(t => t.id === entry.template);
    return template ? template.name : "Okänd mall";
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Use custom entries if available, otherwise generate from template
  const journalEntries = entry.customJournalEntries || generateJournalEntries();
  const totalDebit = journalEntries.reduce((sum, entry) => sum + (entry.debit || 0), 0);
  const totalCredit = journalEntries.reduce((sum, entry) => sum + (entry.credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) <= 0.01;

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .preview-section {
          background: #f8fafc;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
          border: 1px solid #e2e8f0;
        }
        
        .preview-section.highlight {
          border-color: var(--bs-primary);
          box-shadow: 0 4px 12px var(--rgba-primary-2);
        }
        
        .section-icon {
          width: 16px;
          height: 16px;
          color: #64748b;
        }
        
        .balance-indicator {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
        }
        
        .balance-good {
          background: rgba(104, 227, 101, 0.15);
          color: var(--bs-success);
          border: 1px solid rgba(104, 227, 101, 0.3);
        }

        .balance-bad {
          background: rgba(247, 43, 80, 0.08);
          color: var(--bs-danger);
          border: 1px solid rgba(247, 43, 80, 0.2);
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }
        
        .info-item {
          background: white;
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        
        .info-label {
          font-size: 0.75rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          margin-bottom: 0.25rem;
          font-weight: 500;
        }
        
        .info-value {
          font-size: 0.875rem;
          font-weight: 500;
          color: #1e293b;
        }
        
        .compact-table {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }
        
        .compact-table-header {
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          padding: 1rem 1.5rem;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          color: #64748b;
        }
        
        .compact-table-row {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.875rem;
          transition: background-color 0.15s ease;
        }
        
        .compact-table-row:hover {
          background: #f8fafc;
        }
        
        .compact-table-row:last-child {
          border-bottom: none;
        }
        
        .compact-table-footer {
          background: #f1f5f9;
          padding: 1rem 1.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          border-top: 2px solid #e2e8f0;
        }
        
        .amount-cell {
          font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
          font-size: 0.875rem;
        }
        
        .attachment-summary {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.875rem;
          color: #64748b;
        }
        
        .attachment-summary.has-files {
          background: var(--rgba-primary-1);
          border-color: var(--rgba-primary-2);
          color: var(--primary-dark);
        }
        
        .edit-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
        }
        
        .edit-modal-content {
          background: white;
          border-radius: 12px;
          width: 95%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .editable-row {
          transition: all 0.2s ease;
        }
        
        .editable-row:hover {
          background: #f8fafc;
        }
        
        .modern-input {
          border-radius: 6px;
          border: 1px solid #d1d5db;
          transition: all 0.15s ease;
          box-shadow: none;
        }
        
        .modern-input:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 2px var(--rgba-primary-2);
          outline: none;
        }
        
        .modern-input:disabled {
          background-color: #f1f5f9;
          border-color: #e2e8f0;
          color: #64748b;
          cursor: not-allowed;
        }
        
        .modern-btn {
          border-radius: 6px;
          transition: all 0.15s ease;
          font-weight: 500;
        }
        
        .modern-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .modern-btn:active {
          transform: translateY(0);
        }
        
        .async-select-container {
          min-width: 200px;
          max-width: 100%;
        }
        
        .async-select-container .react-select__control {
          border-radius: 6px;
          border: 1px solid #d1d5db;
          min-height: 34px;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          box-shadow: none;
        }
        
        .async-select-container .react-select__control:hover {
          border-color: #9ca3af;
        }
        
        .async-select-container .react-select__control--is-focused {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 2px var(--rgba-primary-2);
        }
        
        .async-select-container .react-select__value-container {
          padding: 2px 6px;
        }
        
        .async-select-container .react-select__input-container {
          margin: 0;
          padding: 0;
        }
        
        .async-select-container .react-select__placeholder {
          color: #9ca3af;
          font-size: 0.875rem;
        }
        
        .async-select-container .react-select__single-value {
          color: #1f2937;
          font-size: 0.875rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .async-select-container .react-select__menu {
          z-index: 1060;
          font-size: 0.875rem;
          border-radius: 6px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          border: 1px solid #e5e7eb;
          min-width: 300px;
        }
        
        .async-select-container .react-select__menu-list {
          padding: 4px;
        }
        
        .async-select-container .react-select__option {
          cursor: pointer;
          border-radius: 4px;
          padding: 8px 12px;
          margin: 2px 0;
        }
        
        .async-select-container .react-select__option--is-focused {
          background: #f3f4f6;
          color: #1f2937;
        }
        
        .async-select-container .react-select__option--is-selected {
          background: var(--bs-primary);
          color: white;
        }

        .async-select-container .react-select__option--is-selected.react-select__option--is-focused {
          background: var(--primary-hover);
        }
      `}</style>

      <div className="enhanced-step-content">
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className={`balance-indicator ${isBalanced ? 'balance-good' : 'balance-bad'}`}>
            {isBalanced ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
            {isBalanced ? 'Balanserad' : 'Obalanserad'}
          </div>
        </div>

        {/* Transaction Details */}
        <div className="preview-section">
          <div className="d-flex align-items-center mb-4">
            <Calendar className="section-icon me-2" />
            <h5 className="mb-0 fw-medium">Transaktionsdetaljer</h5>
          </div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Mall som användes</div>
              <div className="info-value">{getTemplateDisplayName()}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Transaktionsdatum</div>
              <div className="info-value">{format(entry.date, "d MMMM yyyy")}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Beskrivning</div>
              <div className="info-value">{entry.description || "Ej angiven"}</div>
            </div>
            {entry.reference && (
              <div className="info-item">
                <div className="info-label">Referens</div>
                <div className="info-value">{entry.reference}</div>
              </div>
            )}
          </div>
        </div>

        {/* Parameters */}
        {entry.parameters && Object.keys(entry.parameters).length > 0 && (
          <div className="preview-section">
            <div className="d-flex align-items-center mb-4">
              <DollarSign className="section-icon me-2" />
              <h5 className="mb-0 fw-medium">Transaktionsvärden</h5>
            </div>
            <div className="info-grid">
              {Object.entries(entry.parameters).map(([key, value]) => {
                const param = entry.templateData?.parameters?.find(p => p.parameter_name === key);
                const label = param ? param.parameter_label : key;
                const displayValue = param?.parameter_type === 'Number' ? formatAmount(value) : value;
                
                return (
                  <div key={key} className="info-item">
                    <div className="info-label">{label}</div>
                    <div className="info-value">{displayValue}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Journal Entries - Main Focus */}
        <div className="preview-section highlight">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <Calculator className="section-icon me-2" />
              <h5 className="mb-0 fw-medium">Kontotransaktioner</h5>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="small text-muted">
                {journalEntries.length} transaktion{journalEntries.length !== 1 ? 'er' : ''}
              </div>
              <button
                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                onClick={openEditModal}
              >
                <Edit3 size={14} />
                Redigera
              </button>
            </div>
          </div>
          
          <div className="compact-table">
            <div className="compact-table-header d-flex">
              <div style={{ flex: '3' }}>Konto</div>
              <div style={{ flex: '1', textAlign: 'right' }}>Debet (SEK)</div>
              <div style={{ flex: '1', textAlign: 'right' }}>Kredit (SEK)</div>
            </div>
            
            {journalEntries.map((item, index) => (
              <div key={item.id || index} className="compact-table-row d-flex align-items-center">
                <div style={{ flex: '3' }}>
                  <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
                    {item.account}
                    {item.accountName && ` - ${item.accountName}`}
                  </div>
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                  {item.debit > 0 ? (
                    <span className="fw-medium text-success">{formatAmount(item.debit)}</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                  {item.credit > 0 ? (
                    <span className="fw-medium text-primary">{formatAmount(item.credit)}</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </div>
              </div>
            ))}
            
            <div className="compact-table-footer d-flex">
              <div style={{ flex: '3' }}>Summa</div>
              <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success">
                {formatAmount(totalDebit)}
              </div>
              <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary">
                {formatAmount(totalCredit)}
              </div>
            </div>
          </div>

          {/* Balance check */}
          {!isBalanced && (
            <div className="alert alert-warning mt-3 d-flex align-items-center">
              <AlertTriangle size={20} className="me-2" />
              <div>
                <strong>Varning:</strong> Total debet ({formatAmount(totalDebit)}) är inte lika med total kredit ({formatAmount(totalCredit)}). 
                Verifikationen är inte balanserad och kan inte sparas.
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        {entry.notes && entry.notes.trim() && (
          <div className="preview-section">
            <div className="d-flex align-items-center mb-4">
              <MessageSquare className="section-icon me-2" />
              <h5 className="mb-0 fw-medium">Ytterligare Anteckningar</h5>
            </div>
            <div className="info-item">
              <div className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
                {entry.notes}
              </div>
            </div>
          </div>
        )}

        {/* Discrete Attachments Summary */}
        <div className={`attachment-summary ${entry.attachments && entry.attachments.length > 0 ? 'has-files' : ''}`}>
          <Paperclip size={16} />
          {entry.attachments && entry.attachments.length > 0 ? (
            <span>
              <strong>{entry.attachments.length}</strong> bil{entry.attachments.length > 1 ? 'agor' : 'aga'} bifogad{entry.attachments.length > 1 ? 'e' : ''} 
              ({formatFileSize(entry.attachments.reduce((total, att) => total + att.size, 0))})
            </span>
          ) : (
            <span>Inga bilagor bifogade</span>
          )}
        </div>

        {/* Edit Journal Entries Modal */}
        {showEditModal && (
          <div className="edit-modal" onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}>
            <div className="edit-modal-content">
              <div className="d-flex align-items-center justify-content-between p-4 border-bottom">
                <div>
                  <h4 className="mb-1 fw-medium">Redigera Kontotransaktioner</h4>
                  <p className="text-muted mb-0 small">Ändra, lägg till eller ta bort kontotransaktioner. Varje rad kan ha antingen debet ELLER kredit, inte båda.</p>
                </div>
                <button
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                  onClick={() => setShowEditModal(false)}
                >
                  <X size={16} />
                  Stäng
                </button>
              </div>

              <div className="p-4">
                {/* Modern Compact Table for Modal */}
                <div className="compact-table modal-table">
                  <div className="compact-table-header d-flex">
                    <div style={{ flex: '2.5' }}>Konto</div>
                    <div style={{ flex: '1', textAlign: 'center' }}>Debet (SEK)</div>
                    <div style={{ flex: '1', textAlign: 'center' }}>Kredit (SEK)</div>
                    <div style={{ flex: '0.8', textAlign: 'center' }}>Åtgärder</div>
                  </div>
                  
                  {editableEntries.map((entry, index) => (
                    <div key={entry.id} className="compact-table-row modal-row d-flex align-items-center">
                      <div style={{ flex: '2.5' }} className="pe-2">
                        <div className="async-select-container">
                          <AsyncSelect
                            cacheOptions
                            loadOptions={loadAccountOptions}
                            defaultOptions={accounts}
                            value={entry.accountOption}
                            onChange={(selectedOption) => updateEntry(entry.id, 'accountOption', selectedOption)}
                            placeholder="Sök konton..."
                            isClearable
                            className="react-select-container"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: base => ({ ...base, zIndex: 9999 })
                            }}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue ? `Inga konton hittades för "${inputValue}"` : "Skriv för att söka konton"
                            }
                            loadingMessage={() => "Laddar konton..."}
                            formatOptionLabel={(option) => (
                              <div className="d-flex align-items-center">
                                <span className="fw-medium me-2">{option.value}</span>
                                <span className="text-muted text-truncate" style={{ maxWidth: '200px' }}>
                                  - {option.label.split(' - ')[1] || option.label}
                                </span>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                      
                      <div style={{ flex: '1' }} className="px-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm modern-input"
                          value={entry.debit || ''}
                          onChange={(e) => updateEntry(entry.id, 'debit', parseFloat(e.target.value) || 0)}
                          onFocus={(e) => e.target.select()}
                          placeholder="0,00"
                          disabled={entry.credit > 0}
                          style={{
                            backgroundColor: entry.credit > 0 ? '#f1f5f9' : 'white',
                            borderColor: entry.credit > 0 ? '#e2e8f0' : '#d1d5db',
                            cursor: entry.credit > 0 ? 'not-allowed' : 'text',
                            textAlign: 'right',
                            fontFamily: 'SF Mono, Monaco, Cascadia Code, Roboto Mono, Consolas, Courier New, monospace',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ flex: '1' }} className="px-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm modern-input"
                          value={entry.credit || ''}
                          onChange={(e) => updateEntry(entry.id, 'credit', parseFloat(e.target.value) || 0)}
                          onFocus={(e) => e.target.select()}
                          placeholder="0,00"
                          disabled={entry.debit > 0}
                          style={{
                            backgroundColor: entry.debit > 0 ? '#f1f5f9' : 'white',
                            borderColor: entry.debit > 0 ? '#e2e8f0' : '#d1d5db',
                            cursor: entry.debit > 0 ? 'not-allowed' : 'text',
                            textAlign: 'right',
                            fontFamily: 'SF Mono, Monaco, Cascadia Code, Roboto Mono, Consolas, Courier New, monospace',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ flex: '0.8' }} className="ps-1">
                        <div className="d-flex justify-content-center gap-1">
                          {((entry.debit > 0 && entry.credit > 0) || (entry.debit > 0 || entry.credit > 0)) && (
                            <button
                              className="btn btn-outline-warning btn-sm modern-btn"
                              onClick={() => {
                                updateEntry(entry.id, 'debit', 0);
                                updateEntry(entry.id, 'credit', 0);
                              }}
                              title="Rensa belopp"
                              style={{ 
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.375rem',
                                borderRadius: '4px'
                              }}
                            >
                              <X size={10} />
                            </button>
                          )}
                          <button
                            className="btn btn-outline-danger btn-sm modern-btn"
                            onClick={() => removeEntry(entry.id)}
                            title="Ta bort rad"
                            style={{ 
                              fontSize: '0.75rem',
                              padding: '0.25rem 0.375rem',
                              borderRadius: '4px'
                            }}
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="compact-table-footer d-flex">
                    <div style={{ flex: '2.5' }} className="fw-bold">Summa</div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success fw-bold">
                      {formatAmount(calculateEditableTotals().totalDebit)}
                    </div>
                    <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary fw-bold">
                      {formatAmount(calculateEditableTotals().totalCredit)}
                    </div>
                    <div style={{ flex: '0.8' }}></div>
                  </div>
                </div>

                {/* Balance indicator in modal */}
                <div className="d-flex align-items-center justify-content-between mt-4 mb-4">
                  <button
                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 modern-btn"
                    onClick={addNewEntry}
                    style={{ borderRadius: '8px' }}
                  >
                    <Plus size={16} />
                    Lägg till rad
                  </button>

                  <div className={`balance-indicator ${Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit) <= 0.01 ? 'balance-good' : 'balance-bad'}`}>
                    {Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit) <= 0.01 ? (
                      <>
                        <CheckCircle size={16} />
                        Balanserad
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={16} />
                        Obalanserad (Skillnad: {formatAmount(Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit))})
                      </>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-3 justify-content-end">
                  <button
                    className="btn btn-secondary modern-btn"
                    onClick={() => setShowEditModal(false)}
                    style={{ borderRadius: '8px' }}
                  >
                    Avbryt
                  </button>
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2 modern-btn"
                    onClick={saveEditedEntries}
                    disabled={Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit) > 0.01}
                    style={{ borderRadius: '8px' }}
                  >
                    <Save size={16} />
                    Spara ändringar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PreviewStep;



// import React, { useState } from "react";
// import { format } from "date-fns";
// import AsyncSelect from "react-select/async";
// import {
//   File,
//   CheckCircle,
//   Calendar,
//   DollarSign,
//   FileText,
//   AlertTriangle,
//   Eye,
//   Hash,
//   MessageSquare,
//   Calculator,
//   Image,
//   Paperclip,
//   Edit3,
//   Plus,
//   Trash2,
//   Save,
//   X
// } from "lucide-react";

// const PreviewStep = ({
//   entry,
//   onChange,
//   templates,
//   accounts = [],
//   accountsLoading = false,
//   accountsError = null,
//   getAccountByNumber,
//   getAccountsByType,
//   searchAccounts
// }) => {
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [editableEntries, setEditableEntries] = useState([]);

//   const formatAmount = (amount) => {
//     return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
//   };

//   // Evaluate formula with parameters
//   const evaluateFormula = (formula, parameters) => {
//     if (!formula) return 0;

//     try {
//       let expression = formula;
//       Object.keys(parameters).forEach(param => {
//         const value = parameters[param] || 0;
//         expression = expression.replace(new RegExp(param, 'g'), value);
//       });

//       return eval(expression);
//     } catch (error) {
//       console.error('Error evaluating formula:', formula, error);
//       return 0;
//     }
//   };

//   // Generate journal entries from template
//   // In the generateJournalEntries function, add sorting at the end
//   const generateJournalEntries = () => {
//     if (!entry.templateData || !entry.templateData.transactions) {
//       return [];
//     }

//     const entries = [];

//     entry.templateData.transactions.forEach((transaction, index) => {
//       let accountNumber = transaction.account_number;
//       let accountName = transaction.account_name;

//       if (transaction.is_user_defined) {
//         const userAccountKey = `user_account_${index}`;
//         const selectedAccount = entry.userDefinedAccounts?.[userAccountKey];
//         if (selectedAccount) {
//           accountNumber = selectedAccount;
//           const accountInfo = accounts.find(acc => acc.value === selectedAccount);
//           accountName = accountInfo ? accountInfo.label.split(' - ')[1] : `Konto ${selectedAccount}`;
//         } else {
//           accountNumber = "Ej valt";
//           accountName = "";
//         }
//       }

//       const debitAmount = transaction.debit_formula
//         ? evaluateFormula(transaction.debit_formula, entry.parameters)
//         : 0;

//       const creditAmount = transaction.credit_formula
//         ? evaluateFormula(transaction.credit_formula, entry.parameters)
//         : 0;

//       if (debitAmount > 0 || creditAmount > 0) {
//         entries.push({
//           id: `entry_${index}`,
//           account: accountNumber,
//           accountName: accountName,
//           debit: debitAmount,
//           credit: creditAmount,
//           isUserDefined: transaction.is_user_defined,
//           originalIndex: index
//         });
//       }
//     });

//     // Sort entries by account number
//     return entries.sort((a, b) => {
//       // Handle "Ej valt" entries - put them at the end
//       if (a.account === "Ej valt" && b.account !== "Ej valt") return 1;
//       if (b.account === "Ej valt" && a.account !== "Ej valt") return -1;
//       if (a.account === "Ej valt" && b.account === "Ej valt") return 0;

//       // For valid account numbers, sort numerically
//       const accountA = parseInt(a.account, 10);
//       const accountB = parseInt(b.account, 10);

//       // If both are valid numbers, sort numerically
//       if (!isNaN(accountA) && !isNaN(accountB)) {
//         return accountA - accountB;
//       }

//       // If one or both are not numbers, sort alphabetically
//       return a.account.localeCompare(b.account);
//     });
//   };

//   // Update loadAccountOptions to use searchAccounts
//   const loadAccountOptions = (inputValue) => {
//     return new Promise((resolve) => {
//       const filteredOptions = searchAccounts ? searchAccounts(inputValue) :
//         accounts.filter(account =>
//           account.label.toLowerCase().includes(inputValue.toLowerCase()) ||
//           account.value.includes(inputValue)
//         );

//       setTimeout(() => {
//         resolve(filteredOptions);
//       }, 200);
//     });
//   };

//   // Get current account option for AsyncSelect
//   const getCurrentAccountOption = (accountNumber) => {
//     if (!accountNumber || accountNumber === "Ej valt") return null;

//     if (getAccountByNumber) {
//       return getAccountByNumber(accountNumber);
//     }

//     // Fallback search in accounts array
//     return accounts.find(acc => acc.accountNumber === accountNumber || acc.value === accountNumber) ||
//       { value: accountNumber, label: `${accountNumber} - Okänt konto` };
//   };

//   // Open edit modal with current entries
//   const openEditModal = () => {
//     const currentEntries = generateJournalEntries();
//     const entriesWithAccountOptions = currentEntries.map(entry => ({
//       ...entry,
//       accountOption: getCurrentAccountOption(entry.account)
//     }));
//     setEditableEntries(entriesWithAccountOptions);
//     setShowEditModal(true);
//   };

//   // Add new journal entry
//   const addNewEntry = () => {
//     const newEntry = {
//       id: `custom_${Date.now()}`,
//       account: "",
//       accountName: "",
//       accountOption: null,
//       debit: 0,
//       credit: 0,
//       isUserDefined: true,
//       isCustom: true
//     };
//     setEditableEntries([...editableEntries, newEntry]);
//   };

//   // Remove journal entry
//   const removeEntry = (entryId) => {
//     setEditableEntries(editableEntries.filter(entry => entry.id !== entryId));
//   };

//   const updateEntry = (entryId, field, value) => {
//     setEditableEntries(editableEntries.map(entry => {
//       if (entry.id === entryId) {
//         const updatedEntry = { ...entry, [field]: value };

//         // If account option is changed, update account number and name
//         if (field === 'accountOption') {
//           if (value) {
//             updatedEntry.account = value.value;
//             updatedEntry.accountName = value.label.split(' - ')[1] || '';
//           } else {
//             updatedEntry.account = "";
//             updatedEntry.accountName = "";
//           }
//         }

//         // If debit is entered, clear credit
//         if (field === 'debit' && value > 0) {
//           updatedEntry.credit = 0;
//         }

//         // If credit is entered, clear debit
//         if (field === 'credit' && value > 0) {
//           updatedEntry.debit = 0;
//         }

//         return updatedEntry;
//       }
//       return entry;
//     }));
//   };

//   const saveEditedEntries = () => {
//     // Sort the entries before saving
//     const sortedEntries = [...editableEntries].sort((a, b) => {
//       // Handle "Ej valt" entries - put them at the end
//       if (a.account === "Ej valt" && b.account !== "Ej valt") return 1;
//       if (b.account === "Ej valt" && a.account !== "Ej valt") return -1;
//       if (a.account === "Ej valt" && b.account === "Ej valt") return 0;

//       // For valid account numbers, sort numerically
//       const accountA = parseInt(a.account, 10);
//       const accountB = parseInt(b.account, 10);

//       // If both are valid numbers, sort numerically
//       if (!isNaN(accountA) && !isNaN(accountB)) {
//         return accountA - accountB;
//       }

//       // If one or both are not numbers, sort alphabetically
//       return a.account.localeCompare(b.account);
//     });

//     onChange({
//       ...entry,
//       customJournalEntries: sortedEntries
//     });

//     setShowEditModal(false);
//   };

//   // Calculate totals for editable entries
//   const calculateEditableTotals = () => {
//     const totalDebit = editableEntries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0);
//     const totalCredit = editableEntries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0);
//     return { totalDebit, totalCredit };
//   };

//   // Find template display name
//   const getTemplateDisplayName = () => {
//     if (entry.templateData) {
//       return entry.templateData.template_name;
//     }

//     const template = templates.find(t => t.id === entry.template);
//     return template ? template.name : "Okänd mall";
//   };

//   // Get file icon based on type
//   const getFileIcon = (type) => {
//     if (type.startsWith('image/')) return <Image size={20} className="text-success" />;
//     if (type.includes('pdf')) return <FileText size={20} className="text-danger" />;
//     return <File size={20} className="text-primary" />;
//   };

//   // Format file size
//   const formatFileSize = (bytes) => {
//     if (bytes === 0) return '0 Bytes';
//     const k = 1024;
//     const sizes = ['Bytes', 'KB', 'MB', 'GB'];
//     const i = Math.floor(Math.log(bytes) / Math.log(k));
//     return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
//   };

//   // Get Swedish entry type display
//   const getEntryTypeDisplay = (entryType) => {
//     const types = {
//       'income': 'Intäkt',
//       'expense': 'Kostnad',
//       'transfer': 'Överföring',
//       'other': 'Övrigt'
//     };
//     return types[entryType] || entryType;
//   };

//   // Use custom entries if available, otherwise generate from template
//   const journalEntries = entry.customJournalEntries || generateJournalEntries();
//   const totalDebit = journalEntries.reduce((sum, entry) => sum + (entry.debit || 0), 0);
//   const totalCredit = journalEntries.reduce((sum, entry) => sum + (entry.credit || 0), 0);
//   const isBalanced = Math.abs(totalDebit - totalCredit) <= 0.01;

//   return (
//     <div>
//       <style>{`
//         .preview-section {
//           background: #f8fafc;
//           border-radius: 12px;
//           padding: 1.5rem;
//           margin-bottom: 1.5rem;
//           border: 1px solid #e2e8f0;
//         }
        
//         .section-icon {
//           width: 24px;
//           height: 24px;
//           color: #64748b;
//         }
        
//         .balance-indicator {
//           display: inline-flex;
//           align-items: center;
//           gap: 0.5rem;
//           padding: 0.5rem 1rem;
//           border-radius: 8px;
//           font-weight: 500;
//           font-size: 0.875rem;
//         }
        
//         .balance-good {
//           background: #dcfce7;
//           color: #166534;
//           border: 1px solid #bbf7d0;
//         }
        
//         .balance-bad {
//           background: #fef2f2;
//           color: #dc2626;
//           border: 1px solid #fecaca;
//         }
        
//         .info-grid {
//           display: grid;
//           grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
//           gap: 1.5rem;
//         }
        
//         .info-item {
//           background: white;
//           padding: 1rem;
//           border-radius: 8px;
//           border: 1px solid #e2e8f0;
//         }
        
//         .info-label {
//           font-size: 0.75rem;
//           color: #64748b;
//           text-transform: uppercase;
//           letter-spacing: 0.025em;
//           margin-bottom: 0.25rem;
//           font-weight: 500;
//         }
        
//         .info-value {
//           font-size: 0.875rem;
//           font-weight: 500;
//           color: #1e293b;
//         }
        
//         .attachment-grid {
//           display: grid;
//           grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
//           gap: 1rem;
//         }
        
//         .attachment-card {
//           background: white;
//           border: 1px solid #e2e8f0;
//           border-radius: 8px;
//           padding: 1rem;
//           transition: all 0.2s ease;
//         }
        
//         .attachment-card:hover {
//           border-color: #3b82f6;
//           box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
//           transform: translateY(-1px);
//         }
        
//         .compact-table {
//           background: white;
//           border-radius: 8px;
//           overflow: hidden;
//           border: 1px solid #e2e8f0;
//         }
        
//         .compact-table-header {
//           background: #f8fafc;
//           border-bottom: 1px solid #e2e8f0;
//           padding: 0.75rem 1rem;
//           font-size: 0.75rem;
//           font-weight: 600;
//           text-transform: uppercase;
//           letter-spacing: 0.025em;
//           color: #64748b;
//         }
        
//         .compact-table-row {
//           padding: 0.75rem 1rem;
//           border-bottom: 1px solid #f1f5f9;
//           font-size: 0.875rem;
//           transition: background-color 0.15s ease;
//         }
        
//         .compact-table-row:hover {
//           background: #f8fafc;
//         }
        
//         .compact-table-row:last-child {
//           border-bottom: none;
//         }
        
//         .compact-table-footer {
//           background: #f1f5f9;
//           padding: 0.75rem 1rem;
//           font-weight: 600;
//           font-size: 0.875rem;
//           border-top: 2px solid #e2e8f0;
//         }
        
//         .amount-cell {
//           font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
//           font-size: 0.875rem;
//         }
        
//         .summary-stats {
//           background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//           color: white;
//           border-radius: 12px;
//           padding: 1.5rem;
//           margin-bottom: 1.5rem;
//         }
        
//         .edit-modal {
//           position: fixed;
//           top: 0;
//           left: 0;
//           right: 0;
//           bottom: 0;
//           background: rgba(0, 0, 0, 0.5);
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           z-index: 1050;
//         }
        
//         .edit-modal-content {
//           background: white;
//           border-radius: 12px;
//           width: 90%;
//           max-width: 900px;
//           max-height: 90vh;
//           overflow-y: auto;
//           box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
//         }
        
//         .editable-row {
//           transition: all 0.2s ease;
//         }
        
//         .editable-row:hover {
//           background: #f8fafc;
//         }
        
//         .action-buttons {
//           display: flex;
//           gap: 0.5rem;
//           align-items: center;
//         }
        
//         .modal-table {
//           margin-bottom: 0;
//           border: none;
//           box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
//         }
        
//         .modal-row {
//           padding: 1rem;
//           transition: all 0.15s ease;
//           min-height: 64px;
//           align-items: center;
//         }
        
//         .modal-row:hover {
//           background: #f8fafc;
//         }
        
//         .modern-input {
//           border-radius: 6px;
//           border: 1px solid #d1d5db;
//           transition: all 0.15s ease;
//           box-shadow: none;
//         }
        
//         .modern-input:focus {
//           border-color: #3b82f6;
//           box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
//           outline: none;
//         }
        
//         .modern-input:disabled {
//           background-color: #f1f5f9;
//           border-color: #e2e8f0;
//           color: #64748b;
//           cursor: not-allowed;
//         }
        
//         .modern-btn {
//           border-radius: 6px;
//           transition: all 0.15s ease;
//           font-weight: 500;
//         }
        
//         .modern-btn:hover {
//           transform: translateY(-1px);
//           box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
//         }
        
//         .modern-btn:active {
//           transform: translateY(0);
//         }
        
//         .async-select-container {
//     min-width: 200px;
//     max-width: 100%;
//   }
  
//   .async-select-container .react-select__control {
//     border-radius: 6px;
//     border: 1px solid #d1d5db;
//     min-height: 34px;
//     font-size: 0.875rem;
//     transition: all 0.15s ease;
//     box-shadow: none;
//   }
  
//   .async-select-container .react-select__control:hover {
//     border-color: #9ca3af;
//   }
  
//   .async-select-container .react-select__control--is-focused {
//     border-color: #3b82f6;
//     box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
//   }
  
//   .async-select-container .react-select__value-container {
//     padding: 2px 6px;
//   }
  
//   .async-select-container .react-select__input-container {
//     margin: 0;
//     padding: 0;
//   }
  
//   .async-select-container .react-select__placeholder {
//     color: #9ca3af;
//     font-size: 0.875rem;
//   }
  
//   .async-select-container .react-select__single-value {
//     color: #1f2937;
//     font-size: 0.875rem;
//     overflow: hidden;
//     text-overflow: ellipsis;
//     white-space: nowrap;
//   }
  
//   .async-select-container .react-select__menu {
//     z-index: 1060;
//     font-size: 0.875rem;
//     border-radius: 6px;
//     box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
//     border: 1px solid #e5e7eb;
//     min-width: 300px;
//   }
  
//   .async-select-container .react-select__menu-list {
//     padding: 4px;
//   }
  
//   .async-select-container .react-select__option {
//     cursor: pointer;
//     border-radius: 4px;
//     padding: 8px 12px;
//     margin: 2px 0;
//   }
  
//   .async-select-container .react-select__option--is-focused {
//     background: #f3f4f6;
//     color: #1f2937;
//   }
  
//   .async-select-container .react-select__option--is-selected {
//     background: #3b82f6;
//     color: white;
//   }
  
//   .async-select-container .react-select__option--is-selected.react-select__option--is-focused {
//     background: #2563eb;
//   }
  
//   .edit-modal-content {
//     background: white;
//     border-radius: 12px;
//     width: 95%;
//     max-width: 800px;
//     max-height: 90vh;
//     overflow-y: auto;
//     box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
//   }
//       `}</style>

//       {/* Header */}
//       <div className="d-flex align-items-center mb-4">
//         <Eye className="me-3" size={28} style={{ color: '#3b82f6' }} />
//         <div>
//           <h2 className="h4 fw-medium mb-1">Förhandsgranska Transaktion</h2>
//           <p className="text-muted mb-0">Granska din verifikation innan slutförande</p>
//         </div>
//       </div>

//       {/* Summary Stats 
//       <div className="summary-stats">
//         <div className="row align-items-center">
//           <div className="col-md-8">
//             <h3 className="h5 mb-2 text-white">{entry.description || "Namnlös transaktion"}</h3>
//             <div className="d-flex align-items-center gap-3 text-white-50">
//               <span>{format(entry.date, "d MMMM yyyy")}</span>
//               <span>•</span>
//               <span>{getEntryTypeDisplay(entry.entryType)}</span>
//               {entry.reference && (
//                 <>
//                   <span>•</span>
//                   <span>Ref: {entry.reference}</span>
//                 </>
//               )}
//             </div>
//           </div>
//           <div className="col-md-4 text-md-end mt-3 mt-md-0">
//             <div className={`balance-indicator ${isBalanced ? 'balance-good' : 'balance-bad'}`}>
//               {isBalanced ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
//               {isBalanced ? 'Balanserad' : 'Obalanserad'}
//             </div>
//           </div>
//         </div>
//       </div>
//       */}

//       {/* Transaction Details */}
//       <div className="preview-section">
//         <div className="d-flex align-items-center mb-3">
//           <Calendar className="section-icon me-2" />
//           <h5 className="mb-0 fw-medium">Transaktionsdetaljer</h5>
//         </div>
//         <div className="info-grid">
//           <div className="info-item">
//             <div className="info-label">Mall som användes</div>
//             <div className="info-value">{getTemplateDisplayName()}</div>
//           </div>
//           <div className="info-item">
//             <div className="info-label">Transaktionsdatum</div>
//             <div className="info-value">{format(entry.date, "EEEE, d MMMM yyyy")}</div>
//           </div>
//           <div className="info-item">
//             <div className="info-label">Typ av verifikation</div>
//             <div className="info-value">{getEntryTypeDisplay(entry.entryType) || "Ej specificerat"}</div>
//           </div>
//           {entry.reference && (
//             <div className="info-item">
//               <div className="info-label">Referens</div>
//               <div className="info-value">{entry.reference}</div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Parameters */}
//       {entry.parameters && Object.keys(entry.parameters).length > 0 && (
//         <div className="preview-section">
//           <div className="d-flex align-items-center mb-3">
//             <DollarSign className="section-icon me-2" />
//             <h5 className="mb-0 fw-medium">Transaktionsvärden</h5>
//           </div>
//           <div className="info-grid">
//             {Object.entries(entry.parameters).map(([key, value]) => {
//               const param = entry.templateData?.parameters?.find(p => p.parameter_name === key);
//               const label = param ? param.parameter_label : key;
//               const displayValue = param?.parameter_type === 'Number' ? formatAmount(value) : value;

//               return (
//                 <div key={key} className="info-item">
//                   <div className="info-label">{label}</div>
//                   <div className="info-value">{displayValue}</div>
//                 </div>
//               );
//             })}
//           </div>
//         </div>
//       )}

//       {/* Journal Entries - Modern Compact Table Style */}
//       <div className="preview-section">
//         <div className="d-flex align-items-center justify-content-between mb-3">
//           <div className="d-flex align-items-center">
//             <Calculator className="section-icon me-2" />
//             <h5 className="mb-0 fw-medium">Kontotransaktioner</h5>
//           </div>
//           <div className="d-flex align-items-center gap-3">
//             <div className="small text-muted">
//               {journalEntries.length} transaktion{journalEntries.length !== 1 ? 'er' : ''}
//             </div>
//             <button
//               className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
//               onClick={openEditModal}
//             >
//               <Edit3 size={14} />
//               Redigera
//             </button>
//           </div>
//         </div>

//         <div className="compact-table">
//           <div className="compact-table-header d-flex">
//             <div style={{ flex: '3' }}>Konto</div>
//             <div style={{ flex: '1', textAlign: 'right' }}>Debet (SEK)</div>
//             <div style={{ flex: '1', textAlign: 'right' }}>Kredit (SEK)</div>
//           </div>

//           {journalEntries.map((item, index) => (
//             <div key={item.id || index} className="compact-table-row d-flex align-items-center">
//               <div style={{ flex: '3' }}>
//                 <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
//                   {item.account}
//                   {item.accountName && ` - ${item.accountName}`}
//                 </div>
//               </div>
//               <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
//                 {item.debit > 0 ? (
//                   <span className="fw-medium text-success">{formatAmount(item.debit)}</span>
//                 ) : (
//                   <span className="text-muted">—</span>
//                 )}
//               </div>
//               <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
//                 {item.credit > 0 ? (
//                   <span className="fw-medium text-primary">{formatAmount(item.credit)}</span>
//                 ) : (
//                   <span className="text-muted">—</span>
//                 )}
//               </div>
//             </div>
//           ))}

//           <div className="compact-table-footer d-flex">
//             <div style={{ flex: '3' }}>Summa</div>
//             <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success">
//               {formatAmount(totalDebit)}
//             </div>
//             <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary">
//               {formatAmount(totalCredit)}
//             </div>
//           </div>
//         </div>

//         {/* Balance check */}
//         {!isBalanced && (
//           <div className="alert alert-warning mt-3 d-flex align-items-center">
//             <AlertTriangle size={20} className="me-2" />
//             <div>
//               <strong>Varning:</strong> Total debet ({formatAmount(totalDebit)}) är inte lika med total kredit ({formatAmount(totalCredit)}).
//               Verifikationen är inte balanserad och kan inte sparas.
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Notes */}
//       {entry.notes && entry.notes.trim() && (
//         <div className="preview-section">
//           <div className="d-flex align-items-center mb-3">
//             <MessageSquare className="section-icon me-2" />
//             <h5 className="mb-0 fw-medium">Ytterligare Anteckningar</h5>
//           </div>
//           <div className="info-item">
//             <div className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
//               {entry.notes}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Attachments */}
//       {entry.attachments && entry.attachments.length > 0 && (
//         <div className="preview-section">
//           <div className="d-flex align-items-center mb-3">
//             <Paperclip className="section-icon me-2" />
//             <h5 className="mb-0 fw-medium">
//               Bilagor ({entry.attachments.length})
//             </h5>
//           </div>
//           <div className="attachment-grid">
//             {entry.attachments.map((attachment) => (
//               <div key={attachment.id} className="attachment-card">
//                 <div className="d-flex align-items-start gap-3">
//                   <div className="flex-shrink-0">
//                     {attachment.preview ? (
//                       <img
//                         src={attachment.preview}
//                         alt={attachment.name}
//                         className="rounded"
//                         style={{ width: '48px', height: '48px', objectFit: 'cover' }}
//                       />
//                     ) : (
//                       <div className="d-flex align-items-center justify-content-center bg-light rounded"
//                         style={{ width: '48px', height: '48px' }}>
//                         {getFileIcon(attachment.type)}
//                       </div>
//                     )}
//                   </div>
//                   <div className="flex-grow-1 min-w-0">
//                     <div className="fw-medium text-truncate mb-1">
//                       {attachment.name}
//                     </div>
//                     <div className="small text-muted">
//                       {formatFileSize(attachment.size)}
//                     </div>
//                     <div className="small text-muted text-capitalize">
//                       {attachment.type.split('/')[0]}-fil
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       )}


//       {/* Edit Journal Entries Modal */}
//       {showEditModal && (
//         <div className="edit-modal" onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}>
//           <div className="edit-modal-content">
//             <div className="d-flex align-items-center justify-content-between p-4 border-bottom">
//               <div>
//                 <h4 className="mb-1 fw-medium">Redigera Kontotransaktioner</h4>
//                 <p className="text-muted mb-0 small">Ändra, lägg till eller ta bort kontotransaktioner. Varje rad kan ha antingen debet ELLER kredit, inte båda.</p>
//               </div>
//               <button
//                 className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
//                 onClick={() => setShowEditModal(false)}
//               >
//                 <X size={16} />
//                 Stäng
//               </button>
//             </div>

//             <div className="p-4">
//               {/* Modern Compact Table for Modal */}
//               <div className="compact-table modal-table">
//                 <div className="compact-table-header d-flex">
//                   <div style={{ flex: '2.5' }}>Konto</div>
//                   <div style={{ flex: '1', textAlign: 'center' }}>Debet (SEK)</div>
//                   <div style={{ flex: '1', textAlign: 'center' }}>Kredit (SEK)</div>
//                   <div style={{ flex: '0.8', textAlign: 'center' }}>Åtgärder</div>
//                 </div>

//                 {editableEntries.map((entry, index) => (
//                   <div key={entry.id} className="compact-table-row modal-row d-flex align-items-center">
//                     <div style={{ flex: '2.5' }} className="pe-2">
//                       <div className="async-select-container">
//                         <AsyncSelect
//                           cacheOptions
//                           loadOptions={loadAccountOptions}
//                           defaultOptions={accounts.slice(0, 10)}
//                           value={entry.accountOption}
//                           onChange={(selectedOption) => updateEntry(entry.id, 'accountOption', selectedOption)}
//                           placeholder="Sök konton..."
//                           isClearable
//                           className="react-select-container"
//                           classNamePrefix="react-select"
//                           noOptionsMessage={({ inputValue }) =>
//                             inputValue ? `Inga konton hittades för "${inputValue}"` : "Skriv för att söka konton"
//                           }
//                           loadingMessage={() => "Laddar konton..."}
//                           formatOptionLabel={(option) => (
//                             <div className="d-flex align-items-center">
//                               <span className="fw-medium me-2">{option.value}</span>
//                               <span className="text-muted text-truncate" style={{ maxWidth: '200px' }}>
//                                 - {option.label.split(' - ')[1] || option.label}
//                               </span>
//                             </div>
//                           )}
//                         />
//                       </div>
//                     </div>

//                     <div style={{ flex: '1' }} className="px-1">
//                       <input
//                         type="number"
//                         step="0.01"
//                         min="0"
//                         className="form-control form-control-sm modern-input"
//                         value={entry.debit || ''}
//                         onChange={(e) => updateEntry(entry.id, 'debit', parseFloat(e.target.value) || 0)}
//                         onFocus={(e) => e.target.select()}
//                         placeholder="0,00"
//                         disabled={entry.credit > 0}
//                         style={{
//                           backgroundColor: entry.credit > 0 ? '#f1f5f9' : 'white',
//                           borderColor: entry.credit > 0 ? '#e2e8f0' : '#d1d5db',
//                           cursor: entry.credit > 0 ? 'not-allowed' : 'text',
//                           textAlign: 'right',
//                           fontFamily: 'SF Mono, Monaco, Cascadia Code, Roboto Mono, Consolas, Courier New, monospace',
//                           fontSize: '0.875rem'
//                         }}
//                       />
//                     </div>

//                     <div style={{ flex: '1' }} className="px-1">
//                       <input
//                         type="number"
//                         step="0.01"
//                         min="0"
//                         className="form-control form-control-sm modern-input"
//                         value={entry.credit || ''}
//                         onChange={(e) => updateEntry(entry.id, 'credit', parseFloat(e.target.value) || 0)}
//                         onFocus={(e) => e.target.select()}
//                         placeholder="0,00"
//                         disabled={entry.debit > 0}
//                         style={{
//                           backgroundColor: entry.debit > 0 ? '#f1f5f9' : 'white',
//                           borderColor: entry.debit > 0 ? '#e2e8f0' : '#d1d5db',
//                           cursor: entry.debit > 0 ? 'not-allowed' : 'text',
//                           textAlign: 'right',
//                           fontFamily: 'SF Mono, Monaco, Cascadia Code, Roboto Mono, Consolas, Courier New, monospace',
//                           fontSize: '0.875rem'
//                         }}
//                       />
//                     </div>

//                     <div style={{ flex: '0.8' }} className="ps-1">
//                       <div className="d-flex justify-content-center gap-1">
//                         {((entry.debit > 0 && entry.credit > 0) || (entry.debit > 0 || entry.credit > 0)) && (
//                           <button
//                             className="btn btn-outline-warning btn-sm modern-btn"
//                             onClick={() => {
//                               updateEntry(entry.id, 'debit', 0);
//                               updateEntry(entry.id, 'credit', 0);
//                             }}
//                             title="Rensa belopp"
//                             style={{
//                               fontSize: '0.75rem',
//                               padding: '0.25rem 0.375rem',
//                               borderRadius: '4px'
//                             }}
//                           >
//                             <X size={10} />
//                           </button>
//                         )}
//                         <button
//                           className="btn btn-outline-danger btn-sm modern-btn"
//                           onClick={() => removeEntry(entry.id)}
//                           title="Ta bort rad"
//                           style={{
//                             fontSize: '0.75rem',
//                             padding: '0.25rem 0.375rem',
//                             borderRadius: '4px'
//                           }}
//                         >
//                           <Trash2 size={10} />
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 ))}

//                 <div className="compact-table-footer d-flex">
//                   <div style={{ flex: '2.5' }} className="fw-bold">Summa</div>
//                   <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success fw-bold">
//                     {formatAmount(calculateEditableTotals().totalDebit)}
//                   </div>
//                   <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary fw-bold">
//                     {formatAmount(calculateEditableTotals().totalCredit)}
//                   </div>
//                   <div style={{ flex: '0.8' }}></div>
//                 </div>
//               </div>

//               {/* Balance indicator in modal */}
//               <div className="d-flex align-items-center justify-content-between mt-4 mb-4">
//                 <button
//                   className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 modern-btn"
//                   onClick={addNewEntry}
//                   style={{ borderRadius: '8px' }}
//                 >
//                   <Plus size={16} />
//                   Lägg till rad
//                 </button>

//                 <div className={`balance-indicator ${Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit) <= 0.01 ? 'balance-good' : 'balance-bad'}`}>
//                   {Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit) <= 0.01 ? (
//                     <>
//                       <CheckCircle size={16} />
//                       Balanserad
//                     </>
//                   ) : (
//                     <>
//                       <AlertTriangle size={16} />
//                       Obalanserad (Skillnad: {formatAmount(Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit))})
//                     </>
//                   )}
//                 </div>
//               </div>

//               <div className="d-flex gap-3 justify-content-end">
//                 <button
//                   className="btn btn-secondary modern-btn"
//                   onClick={() => setShowEditModal(false)}
//                   style={{ borderRadius: '8px' }}
//                 >
//                   Avbryt
//                 </button>
//                 <button
//                   className="btn btn-primary d-flex align-items-center gap-2 modern-btn"
//                   onClick={saveEditedEntries}
//                   disabled={Math.abs(calculateEditableTotals().totalDebit - calculateEditableTotals().totalCredit) > 0.01}
//                   style={{ borderRadius: '8px' }}
//                 >
//                   <Save size={16} />
//                   Spara ändringar
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default PreviewStep;
